/**
 * 错误 → 界面动作。
 *
 * 契约 §3.3 那张表的「前端动作」一列在这里落地。⚠ **按 `code` 分支，不按 HTTP 状态码** ——
 * `AUTH.CODE_TOO_FREQUENT` 与 `COMMON.RATE_LIMITED` 都是 429，处置却相反。
 */
import { isApiError } from '@cione/api-client'
import { messageKeyOf } from '@cione/contracts'

/** 表单里可能被标红的字段。 */
export type AuthField = 'phone' | 'smsCode' | 'password' | 'newPassword'

export type ErrorAction =
  /** 字段级标红。`fieldErrors` 非空时也走这里(契约 §2.3：非空 ⇒ 表单能改的问题)。 */
  | { kind: 'field'; field: AuthField; messageKey: string; traceId: string }
  /** 倒计时禁用按钮。⚠ 不要因为 429 就自动重试 —— 它 retryable=false，重试只会继续撞冷却。 */
  | { kind: 'countdown'; messageKey: string; traceId: string }
  /** 提示重新获取验证码。 */
  | { kind: 'refetch-code'; messageKey: string; traceId: string }
  /** 清本地令牌并弹登录。 */
  | { kind: 'relogin'; messageKey: string; traceId: string }
  /** 匿名越界 → 引导登录。 */
  | { kind: 'guide-login'; messageKey: string; traceId: string }
  /** 终态，不给重试。 */
  | { kind: 'terminal'; messageKey: string; traceId: string }
  /** 其余：整体提示 + 显示 traceId(表外的码一律落这里)。 */
  | { kind: 'notice'; messageKey: string; traceId: string }

const FIELD_OF_CODE: Partial<Record<string, AuthField>> = {
  'AUTH.CODE_INVALID': 'smsCode',
  'AUTH.PASSWORD_INVALID': 'password',
}

export function describeApiError(error: unknown): ErrorAction {
  if (!isApiError(error)) {
    return { kind: 'notice', messageKey: 'error.unknown', traceId: '' }
  }
  const base = { messageKey: messageKeyOf(error.code), traceId: error.traceId }

  // 契约 §2.3：fieldErrors 非空 ⇒ 一定是表单能改的问题，优先做字段级高亮
  const firstField = error.fieldErrors[0]
  if (firstField) {
    return { kind: 'field', field: asAuthField(firstField.field), ...base }
  }

  switch (error.code) {
    case 'AUTH.CODE_INVALID':
    case 'AUTH.PASSWORD_INVALID':
      return { kind: 'field', field: FIELD_OF_CODE[error.code] ?? 'phone', ...base }
    case 'AUTH.CODE_TOO_FREQUENT':
      return { kind: 'countdown', ...base }
    case 'AUTH.CODE_EXPIRED':
      return { kind: 'refetch-code', ...base }
    case 'AUTH.LOGIN_REQUIRED':
    case 'AUTH.TOKEN_INVALID':
    case 'AUTH.TOKEN_EXPIRED':
      // ⚠ TOKEN_EXPIRED 正常走不到这里 —— HTTP 层会先静默刷新。
      //   能走到，说明刷新也没救回来，那就等于要重新登录。
      return { kind: 'relogin', ...base }
    case 'AUTH.ACTION_FORBIDDEN':
      return { kind: 'guide-login', ...base }
    case 'AUTH.ACCOUNT_DISABLED':
      return { kind: 'terminal', ...base }
    default:
      return { kind: 'notice', ...base }
  }
}

/**
 * 「这个错误码是不是在要求用户去登录」。
 *
 * ⚠ 与 {@link describeApiError} 是**同一张表** —— `relogin` 与 `guide-login`
 *   两类的并集，不是另立一份码清单(`auth-errors.test.ts` 里有一条用例逐码对账)。
 *
 * ⚠ 为什么要这个入口：SSE 上的 `agent_error` 只给到一个 `code` 字符串，
 *   手里没有 `ApiError` 可以喂给 `describeApiError`。
 *
 * ⚠ `AUTH.TOKEN_EXPIRED` 在里面是对的：能走到需要判断的地方，说明 HTTP 层的
 *   静默刷新已经试过并且失败了(见 api-client 的 `REFRESHABLE_CODES`) ——
 *   那时它的含义就是「重新登录」，不会打扰「聊到一半自动续期」那条正常路径。
 */
export function needsLogin(code: string): boolean {
  switch (code) {
    case 'AUTH.LOGIN_REQUIRED':
    case 'AUTH.TOKEN_INVALID':
    case 'AUTH.TOKEN_EXPIRED':
    case 'AUTH.ACTION_FORBIDDEN':
      return true
    default:
      return false
  }
}

/**
 * 「服务端刚说了**这个身份不作数**」—— 本地登录态必须跟着作废（W1-5）。
 *
 * ⚠ 它是 {@link needsLogin} 的**真子集**，不是另一张表：
 *   `needsLogin` 回答「要不要把用户引去登录」，本函数回答「本地那份登录态还算不算数」。
 *   两者对 `ACTION_FORBIDDEN` 与 `TOKEN_EXPIRED` 的答案不同，所以必须分开：
 *
 *   - `ACTION_FORBIDDEN`：令牌**完全有效**，只是等级不够。清了反而把一个好令牌扔了。
 *   - `TOKEN_EXPIRED`：HTTP 层的静默刷新会续上（`REFRESHABLE_CODES`）。
 *     ⚠⚠ 它从**流**上过来时尤其不能清 —— 那正是「用户聊到一半被莫名弹一次登录」的由来。
 *   - `LOGIN_REQUIRED` / `TOKEN_INVALID`：刷新救不回来（两个都不在 `REFRESHABLE_CODES` 里），
 *     服务端就是说这个身份没了。本地还当自己是 USER 的话，后面每一次发送都会撞同一堵墙。
 */
export function rejectsSession(code: string): boolean {
  return code === 'AUTH.LOGIN_REQUIRED' || code === 'AUTH.TOKEN_INVALID'
}

/**
 * 气泡里该显示哪句话。
 *
 * ⚠⚠ 与 {@link messageKeyOf} 只差一个码，但差得有理由（W1-5）：
 *   **气泡里出现的 `AUTH.*` 一定来自流**。请求层的鉴权失败走 `handleLoginRequired`，
 *   它会把那两条气泡整个撤掉，压根不会留下一条带 `AUTH.*` 的消息。
 *   而流上的 `AUTH.TOKEN_EXPIRED` 与请求层的含义**正好相反**：
 *   请求层能拿到它 = 静默刷新已经失败（那就真该重新登录）；
 *   流上拿到它 = 令牌刚好在这一轮里过期，下一个请求就会自动续上（不用做任何事）。
 *   两处共用「登录已过期，请重新登录」的话，用户会白登一次录。
 *
 * ⚠ 其余的码原样落回 `messageKeyOf` —— 不在这里另开一张文案表。
 */
export function chatErrorMessageKey(code: string): string {
  if (code === 'AUTH.TOKEN_EXPIRED') return 'chat.tokenExpiredRetry'
  return messageKeyOf(code)
}

/**
 * 这条错误之后，「重新发送」帮得上忙吗。
 *
 * ⚠ 这是一张**白名单**，不是黑名单 —— 默认不给按钮。
 *   给一个点了必然还是失败的按钮，比不给更糟（用户会一直点）。
 * ⚠ 为什么 `AUTH.ACTION_FORBIDDEN` 不在里面：令牌有效、只是等级不够，
 *   重试一百次也过不去（BE-2 在 `build.py` 里写的就是这句话）。
 * ⚠ 为什么 `AUTH.LOGIN_REQUIRED` / `TOKEN_INVALID` 也不在：那两个码会弹出登录框，
 *   用户的下一步是登录，不是重发。等登录完再给重发入口是另一件事，没顺手做。
 */
export function resendMayHelp(code: string): boolean {
  return code === 'AUTH.TOKEN_EXPIRED'
}

function asAuthField(field: string): AuthField {
  switch (field) {
    case 'phone':
    case 'smsCode':
    case 'password':
    case 'newPassword':
      return field
    default:
      // 后端标了一个我们界面上没有的字段：退回手机号那一栏，总比不显示好
      return 'phone'
  }
}
